/* eslint-disable no-console */
import React, { Fragment, useState } from 'react'
import injectStyles from 'react-jss'
import { Button } from '@material-ui/core'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { PageTitle } from 'Components/PageTitle'

import { BreadcrumbsUrlShortener } from './BreadcrumbsUrlShortener'

import { styles } from './styles'
import DropZoneArea from '../../Campaigns/Page/Codes/components/Popups/CodeUploadPopups/DropZoneArea'
import { downloadStringAsFile, parseCSV } from '../../../Utils/Misc'
import { apiClient, sleep } from '../../../Module/API/Client'
import { escapeCsvString } from '../../../shared/csv'
import { CONFIG } from '../../../Config/config'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import { useDispatch } from 'react-redux'
import { showReduxModal } from '../../../Reducers/UI/actions'
import { splitArrayIntoChunks } from '../../../shared/libraries/utils/array'
import { Checkbox, SubmitButton } from '../../../Components/Form'
import useGraphQLFetch from '../../../Utils/Hooks/useGraphQLFetch'
import { getCampaigns as getCampaignsGQL } from '../../../Module/graphQL'
import { InputV2 } from '../../../Components/Form/InputV2'

const downloadTemplate = createQrLinks => {
  const content = createQrLinks
    ? 'id,url,flyer_code\nABC,https://oppizi.com,ABC'
    : 'id,url\nABC,https://oppizi.com'
  return downloadStringAsFile(content, 'shortened_url_template.csv', 'text/csv')
}

const processUrl = (url, linksType, queryParams) => {
  if (linksType !== 'shorten') {
    if (!url.includes('http')) {
      alert(
        'Please enter a valid URL, it should start with http:// or https://',
      )
      throw new Error('Invalid URL')
    }
    const urlObject = new URL('https://api.oppizi.com/external-redirect')

    urlObject.searchParams.set('redirectURL', url)

    if (linksType === 'app') {
      urlObject.searchParams.set('variant', 'intermidiate_page')
    }

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        urlObject.searchParams.set(key, value)
      }
    })
    return urlObject.toString()
  }
  return url
}

const UrlShortener = ({ classes }) => {
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const [campaignId, setCampaignId] = useState(null)
  const [linksType, setLinksType] = useState('shorten')
  const [customTitleText, setCustomTitleText] = useState(
    'Flyer scanned successfully',
  )
  const [customButtonText, setCustomButtonText] = useState('Redeem')
  const [customLogoSrc, setCustomLogoSrc] = useState(null)
  const [customTitleColor, setCustomTitleColor] = useState('#000000')
  const [customButtonColor, setCustomButtonColor] = useState('#000000')
  const [customButtonTextColor, setCustomButtonTextColor] = useState('#FFFFFF')
  const [redirectOnLoad, setRedirectOnLoad] = useState(true)

  const { data = [] } = useGraphQLFetch({
    fetchFn: () =>
      getCampaignsGQL({
        statuses: ['ACTIVE', 'COMPLETED', 'DRAFT'],
      }),
    mapResponse: data =>
      data.campaigns.edges
        .map(({ node }) => ({
          cityPlans: node.cityPlans.nodes,
          clientId: node.client.id,
          label: node.name,
          value: node.id,
          name: node.name,
          id: node.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
  })

  const onFileUpload = async files => {
    try {
      setLoading(true)
      const parsedData = await parseCSV(files[0])

      const { data } = parsedData

      const chunks = splitArrayIntoChunks({
        inputArray: data,
        itemsPerChunk: 10000,
      })

      let result = []
      console.log(`UrlShortener received ${chunks.length} chunks`)
      for (let index = 0; index < chunks.length; index++) {
        const rows = chunks[index]
        if (!rows.length) continue
        const formattedData = rows.reduce((acc, row) => {
          acc[row.id] = processUrl(row.url, linksType, {
            campaignId,
            customTitleText,
            customButtonText,
            customLogoSrc,
            customTitleColor,
            customButtonColor,
            customButtonTextColor,
            flyerCode: row.flyer_code,
            redirectOnLoad,
          })
          return acc
        }, {})

        await sleep(1000)
        console.log(`Upload in Progress - ${index}/${chunks.length}`)

        const res = await apiClient.getAndCreateShortUrls(formattedData)
        const resData = await res.json()
        if (resData.error) {
          dispatch(
            showReduxModal({
              title: 'Validation error',
              children: result.message,
              confirmLabel: `OK`,
              hideCancel: true,
            }),
          )
        }
        result = [...result, ...resData]
      }
      console.log('Upload is done, generating the CSV')
      let content = 'id,shortUrl,url'
      result.forEach(
        row =>
          (content +=
            '\n' +
            ['id', 'shortUrl', 'url']
              .map(headerName => {
                let value = row[headerName]

                if (headerName === 'shortUrl') {
                  value = `${CONFIG.API_BASE_URL.replace(
                    '/platform',
                    '/short',
                  )}/${value}`
                }
                return typeof value === 'string'
                  ? escapeCsvString(value)
                  : value
              })
              .join(',')),
      )

      downloadStringAsFile(content, 'shortedUrl.csv', 'text/csv')
      console.log('CSV generated!')
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const onCampaignChange = (e, a, campaignId) => {
    setCampaignId(campaignId)
  }

  return (
    <Fragment>
      <BreadcrumbsUrlShortener className={classes.header} />
      <PageTitle className={classes.header}>Url Shortener</PageTitle>
      <div className={classes.wrapper}>
        <ButtonGroup disableElevation color="primary">
          <SubmitButton
            value="Create print-ready app links"
            variant={linksType === 'app' ? 'contained' : 'outlined'}
            onClick={() => setLinksType('app')}
          />
          <SubmitButton
            value="Create print-ready web links"
            variant={linksType === 'web' ? 'contained' : 'outlined'}
            onClick={() => setLinksType('web')}
          />
          <SubmitButton
            value="Shorten only"
            variant={linksType === 'shorten' ? 'contained' : 'outlined'}
            onClick={() => setLinksType('shorten')}
          />
        </ButtonGroup>
        {linksType !== 'shorten' ? (
          <div className={classes.wrapper} style={{ padding: '0 !important' }}>
            <Autocomplete
              id="campaign-select"
              disabled={!data}
              options={data || []}
              onChange={(event, newValue) =>
                onCampaignChange(event, newValue, newValue.id)
              }
              getOptionLabel={option => option.label || ''}
              style={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Campaign"
                  variant="outlined"
                  required
                  error={!campaignId}
                  helperText={!campaignId ? 'Campaign is required' : ''}
                />
              )}
            />
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {linksType === 'app' && (
                <div
                  className={classes.wrapper}
                  style={{ padding: '0 !important' }}
                >
                  <Checkbox
                    style={{ width: '100%', marginBottom: '10px' }}
                    label={'Use page as a fallback'}
                    initValue={redirectOnLoad}
                    onChange={e => {
                      setRedirectOnLoad(e.target.checked)
                    }}
                  />
                  <InputV2
                    style={{ width: '100%' }}
                    label={'Custom title text'}
                    placeholder="Flyer scanned successfully"
                    value={customTitleText}
                    onChange={e => {
                      setCustomTitleText(e.target.value)
                    }}
                  />
                  <InputV2
                    style={{ width: '100%' }}
                    label={'Custom button text'}
                    placeholder="Redeem"
                    value={customButtonText}
                    onChange={e => {
                      setCustomButtonText(e.target.value)
                    }}
                  />
                  <InputV2
                    style={{ width: '100%' }}
                    label={'Custom logo source'}
                    placeholder="https://oppizi.com/images/oppizi-logo.png"
                    value={customLogoSrc}
                    onChange={e => {
                      setCustomLogoSrc(e.target.value)
                    }}
                    hint="Enter the URL of your custom logo image. You can get it by right clicking on the logo and selecting 'Copy image address' on the client's website"
                  />
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <InputV2
                      style={{ width: '100%' }}
                      label={
                        'Custom title color (RGB, HEX, HSL, RGBA, or HSLA)'
                      }
                      placeholder="black"
                      value={customTitleColor}
                      onChange={e => {
                        setCustomTitleColor(e.target.value)
                      }}
                    />
                    <div
                      style={{
                        width: 50,
                        height: 50,
                        marginTop: 35,
                      }}
                    >
                      <input
                        type="color"
                        value={customTitleColor}
                        onChange={e => {
                          setCustomTitleColor(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: 0,
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <InputV2
                      style={{ width: '100%' }}
                      label={
                        'Custom button color (RGB, HEX, HSL, RGBA, or HSLA)'
                      }
                      placeholder="black"
                      value={customButtonColor}
                      onChange={e => {
                        setCustomButtonColor(e.target.value)
                      }}
                    />
                    <div
                      style={{
                        width: 50,
                        height: 50,
                        marginTop: 35,
                      }}
                    >
                      <input
                        type="color"
                        value={customButtonColor}
                        onChange={e => {
                          setCustomButtonColor(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: 0,
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <InputV2
                      style={{ width: '100%' }}
                      label={
                        'Custom button text color (RGB, HEX, HSL, RGBA, or HSLA)'
                      }
                      placeholder="white"
                      value={customButtonTextColor}
                      onChange={e => {
                        setCustomButtonTextColor(e.target.value)
                      }}
                    />
                    <div
                      style={{
                        width: 50,
                        height: 50,
                        marginTop: 35,
                      }}
                    >
                      <input
                        type="color"
                        value={customButtonTextColor}
                        onChange={e => {
                          setCustomButtonTextColor(e.target.value)
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          padding: 0,
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {linksType === 'app' && (
                <section
                  style={{
                    border: '16px solid black',
                    borderRadius: '36px',
                    width: '375px',
                    height: '667px',
                    margin: '40px auto',
                    backgroundColor: 'white',
                    boxShadow: '0px 0px 20px rgba(0,0,0,0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '10rem',
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      fontFamily: 'sans-serif',
                    }}
                  >
                    {customLogoSrc ? (
                      <img
                        src={customLogoSrc}
                        alt="Promofy"
                        style={{
                          width: '150px',
                          height: '150px',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      ''
                    )}
                    <h1
                      style={{
                        textAlign: 'center',
                        color: customTitleColor,
                      }}
                    >
                      {customTitleText}
                    </h1>
                    <br />
                    <a
                      onClick={() => {
                        alert(
                          'It will redirect to the url that you will upload below',
                        )
                      }}
                      id="link"
                      style={{
                        fontSize: '2rem',
                        padding: '2rem',
                        margin: '2rem',
                        fontWeight: 'bold',
                        fontFamily: 'system-ui',
                        backgroundColor: customButtonColor,
                        color: customButtonTextColor,
                        textDecoration: 'none',
                        textAlign: 'center',
                        borderRadius: '12px',
                      }}
                    >
                      {customButtonText}
                    </a>
                  </div>
                </section>
              )}
            </div>
          </div>
        ) : null}

        {loading ? (
          <LoadingIndicator size={50} color="inherit" />
        ) : (
          <DropZoneArea
            onFileUpload={onFileUpload}
            error={error}
            setError={setError}
          />
        )}
        <Button
          color="primary"
          style={{ marginLeft: '1rem' }}
          onClick={downloadTemplate}
        >
          Download template
        </Button>
      </div>
    </Fragment>
  )
}

export default injectStyles(styles)(UrlShortener)
